<template lang="">
<!--~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    Start Server Requirements Page
~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~-->
<div class="android-section doc-wrapper doc-inner">
    <div class="row">
        <div class="col-xl-12">
            <div class="android-wrapper">
                <h2 class="inner-title">Building and Signing APK for Submission to Play Store: A Step-by-Step Guide</h2>
                <small class="mb-30">Building and submitting your Flutter app to the Google Play Store involves several steps, including building, signing, and uploading the APK. Below is a comprehensive guide on how to accomplish this:</small>
                <ul class="configure-list">
                    <li class="mb-10"><b>Step 1:</b> Prepare Your Flutter App
                        <ol>
                            <li>Ensure that your Flutter app is fully developed and tested before proceeding with the submission process. Make sure all necessary configurations, assets, and dependencies are in place.</li>
                        </ol>
                    </li>
                    <li class="mb-10"><b>Step 2:</b> Build the APK
                        <ol>
                            <li>Open your terminal or command prompt.</li>
                            <li>Navigate to your Flutter project directory.</li>
                            <li>Run the following command to build the release version of your APK:</li>
                            <pre class="prettyprint mb-0">flutter build apk --release</pre>
                            <small><span class="text--danger">Note</span> This command generates an APK file ready for release in the <code>build/app/outputs/flutter-apk</code> directory.</small>
                        </ol>
                    </li>
                    <li class="mb-10"><b>Step 3:</b> Generate a Signing Key <br>
                        <small>Before you can upload your app to the Play Store, you need to sign it with a signing key. Follow these steps to generate a signing key:</small>
                        <ol>
                            <li>Open your terminal or command prompt.</li>
                            <li>Navigate to your Flutter project’s <code>android</code> directory.</li>
                            <li>Run the following command to generate a signing key:</li>
                            <pre class="prettyprint mb-0">keytool -genkey -v -keystore key.jks -keyalg RSA -keysize 2048 -validity 10000 -alias key</pre>
                            <small><span class="text--danger">Note</span> Follow the prompts to enter the required information, such as passwords and aliases.</small>
                        </ol>
                    </li>
                    <li class="mb-10"><b>Step 4:</b> Configure Gradle Build Script <br>
                        <small>To sign your app using the generated signing key, you need to configure the Gradle build script. Follow these steps:</small>
                        <ol>
                            <li>Open the <code>android/app/build.gradle</code> file in your Flutter project.</li>
                            <li>Navigate to your Flutter project’s <code>android</code> directory.</li>
                            <li>Add the following code snippet at the end of the file:</li>
                            <pre class="prettyprint mt-0" style="white-space: normal;">
                                <span class="code-show-list">
                                    <span>groovy:</span>
                                    <br>
                                    <span>
                                        <br>android {
                                        <br>&nbsp;signingConfigs {
                                        <br>&nbsp;&nbsp;release {
                                        <br>&nbsp;&nbsp;&nbsp;keyAlias ‘your_alias’
                                        <br>&nbsp;&nbsp;&nbsp;keyPassword ‘your_key_password’
                                        <br>&nbsp;&nbsp;&nbsp;storeFile file(‘path_to_your_keystore_file’)
                                        <br>&nbsp;&nbsp;&nbsp;storePassword ‘your_keystore_password’
                                        <br>&nbsp;&nbsp;}
                                        <br>&nbsp;}
                                        <br>&nbsp;buildTypes {
                                        <br>&nbsp;&nbsp;release {
                                        <br>&nbsp;&nbsp;&nbsp;signingConfig signingConfigs.release
                                        <br>&nbsp;&nbsp;}
                                        <br>&nbsp;}
                                        <br>}
                                    </span>
                                </span>
                            </pre>
                            <small><span class="text--danger">Note</span> Replace <code>‘your_alias’</code>, <code>‘your_key_password’</code>, <code>‘path_to_your_keystore_file’</code>, and <code>‘your_keystore_password’</code> with the appropriate values.</small>
                        </ol>
                    </li>
                    <li class="mb-10"><b>Step 5:</b> Build Signed APK <br>
                        <small>To sign your app using the generated signing key, you need to configure the Gradle build script. Follow these steps:</small>
                        <ol>
                            <li>Open your terminal or command prompt.</li>
                            <li>Navigate to your Flutter project directory.</li>
                            <li>Run the following command to build the signed APK:</li>
                            <pre class="prettyprint mb-0">flutter build apk --release</pre>
                            <small><span class="text--danger">Note</span> This command generates a signed APK file ready for submission to the Play Store.</small>
                        </ol>
                    </li>
                    <li class="mb-10"><b>Step 6:</b> Create a Google Play Developer Account <br>
                        <small>If you don’t already have one, create a Google Play Developer account. You’ll need to pay a one-time registration fee to access the Google Play Console.</small>
                    </li>
                    <li class="mb-10"><b>Step 7:</b> Log in to Google Play Console
                        <ol>
                            <li>Visit the <a href="https://play.google.com/apps/publish/" target="_blank" class="redirect-link">Google Play Console</a> website.</li>
                            <li>Log in using your Google account credentials.</li>
                        </ol>
                    </li>
                    <li class="mb-10"><b>Step 8:</b> Create a New App Release
                        <ol>
                            <li>In the Google Play Console dashboard, select your app.</li>
                            <li>Navigate to the “Release” tab on the left sidebar.</li>
                            <li>Click on “Create Release” or “Manage Production” depending on your app’s current release status.</li>
                        </ol>
                    </li>
                    <li class="mb-10"><b>Step 9:</b> Upload Your APK
                        <ol>
                            <li>Click on “Upload” and select the signed APK file generated earlier.</li>
                            <li>Wait for the upload to complete. Google Play Console will validate the APK for any errors or policy violations.</li>
                        </ol>
                    </li>
                    <li class="mb-10"><b>Step 10:</b> Configure Release Details
                        <ol>
                            <li>Fill in the release details, including the release name, release notes, and any other relevant information.</li>
                            <li>Verify that all information is accurate and <code>up-to-date</code>.</li>
                        </ol>
                    </li>
                    <li class="mb-10"><b>Step 11:</b> Rollout Options <span class="text--base">(Optional)</span><br>
                        <small class="mb-30">You can choose how you want to roll out your release:</small>
                        <ol>
                            <li><b>Gradual rollout:</b> Release the update to a percentage of your users.</li>
                            <li><b>Immediate rollout:</b> Release the update to all users immediately.</li>
                        </ol>
                    </li>
                    <li class="mb-10"><b>Step 12:</b> Review and Publish
                        <ol>
                            <li>Review all the details and configurations carefully.</li>
                            <li>Click on “Review” or “Publish” to submit your release for review.</li>
                        </ol>
                    </li>
                    <li class="mb-10"><b>Step 13:</b> Review Process
                        <ol>
                            <li>Your release will undergo review by Google Play team, which may take a few hours to several days depending on their workload.</li>
                        </ol>
                    </li>
                    <li class="mb-10"><b>Step 14:</b> Distribution
                        <ol>
                            <li>Once your release passes review, it will be available to users on the Google Play Store.</li>
                        </ol>
                    </li>
                    <li class="mb-10"><b>Step 15:</b> Monitor Performance
                        <ol>
                            <li>Monitor your app’s performance using the metrics provided in the Google Play Console. This includes installation metrics, user feedback, and app ratings.</li>
                        </ol>
                    </li>
                    <p><b class="text--danger">Conclusion:</b> By following these steps, you can successfully upload your APK to the Google Play Store and make your app available to millions of users worldwide. Remember to keep your app updated and respond promptly to user feedback to ensure a positive user experience</p>
                </ul>
            </div>
        </div>
    </div>
</div>
<div class="page-change-area">
    <div class="navigation-wrapper">
        <router-link :to="{ name: 'DevelopmentAndTesting'}" class="left"><i class="las la-angle-left me-1"></i> Development and Testing</router-link>
        <router-link :to="{ name: 'iOSAppDeployment'}" class="right">iOS App Deployment <i class="las la-angle-right ms-1"></i></router-link>
    </div>
</div>
<!--~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    End Server Requirements Page
~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~-->
</template>

<script>
export default {
    name: 'AndroidAppDeploymentPage',
}
</script>